import { Component, ChangeDetectionStrategy, inject, computed, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { TooltipModule } from "primeng/tooltip";
import { Popover, PopoverModule } from "primeng/popover";

import { QuoteStatus } from "src/api-service";
import { getQuoteStatusIcon, getQuoteStatusSeverity } from "src/app/shared/util/quote-status.utils";
import { selectIsSubscriptionQuote, selectQuoteState } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { selectLoadingInitializing } from "src/app/store/pages/quote/quote.selectors";
import { selectDrawer, selectDrawingEstimatedCompletion } from "src/app/store/pages/quote/drawing/drawing.selectors";
import { VersionActionsComponent } from "src/app/pages/quote/actions/version-actions/version-actions.component";
import { QuoteStatusTimelineComponent } from "src/app/shared/components/quote-status-timeline/quote-status-timeline.component";
import { DebugInspectDirective } from "src/app/shared/debug-inspect/debug-inspect.directive";

@Component({
    selector: "app-quote-toolbar-view",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        TooltipModule,
        PopoverModule,
        VersionActionsComponent,
        QuoteStatusTimelineComponent,
        DebugInspectDirective,
    ],
    templateUrl: "./quote-toolbar-view.component.html",
    styleUrl: "./quote-toolbar-view.component.scss",
})
export class QuoteToolbarViewComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly quote = toSignal(this.store.select(selectQuoteState));
    public readonly selectedVersion = toSignal(this.store.select(selectVersion));
    public readonly statusLog = computed(() => this.selectedVersion()?.versionStatusChangeLogList ?? []);
    public readonly drawer = toSignal(this.store.select(selectDrawer));
    public readonly estimatedCompletion = toSignal(this.store.select(selectDrawingEstimatedCompletion));
    public readonly loading = toSignal(this.store.select(selectLoadingInitializing), { initialValue: true });
    public readonly isSubscription = toSignal(this.store.select(selectIsSubscriptionQuote), { initialValue: false });

    // =========================================================================
    // COMPUTED
    // =========================================================================
    public readonly quoteId = computed(() => this.quote()?.id);
    public readonly quoteDisplayId = computed(() => this.quote()?.displayId);
    public readonly hasFormerId = computed(() => {
        const displayId = this.quoteDisplayId();
        return !!displayId && displayId !== this.quoteId();
    });
    public readonly quoteStatusIcon = computed(() => getQuoteStatusIcon(this.quote()?.status).icon);
    public readonly quoteStatusSeverity = computed(() => getQuoteStatusSeverity(this.quote()?.status));
    public readonly quoteStatusLabel = computed(() => {
        const status = this.quote()?.status;
        return `VERSION.STATUS.${QuoteStatus[status ?? 0]?.toUpperCase() ?? "DRAFT"}`;
    });
    public readonly customerName = computed(() => this.quote()?.customer?.name ?? "");
    public readonly projectName = computed(() => this.quote()?.opportunity?.name ?? "");

    // ──────────────────────────────────────────────────────────────────────────
    // Status-chip hover-popover
    //
    // Tiny delay on open avoids opening the popover when the user is just
    // tracking past the chip; longer delay on close lets the user move into
    // the popover content without flicker.
    // ──────────────────────────────────────────────────────────────────────────
    @ViewChild("statusPopover") public statusPopover?: Popover;
    private openTimer: number | null = null;
    private closeTimer: number | null = null;

    public onStatusEnter(event: MouseEvent, anchor: HTMLElement): void {
        if (this.closeTimer != null) { window.clearTimeout(this.closeTimer); this.closeTimer = null; }
        if (this.openTimer != null) return;
        this.openTimer = window.setTimeout(() => {
            this.openTimer = null;
            this.statusPopover?.show(event, anchor);
        }, 80);
    }

    public onStatusLeave(): void {
        if (this.openTimer != null) { window.clearTimeout(this.openTimer); this.openTimer = null; }
        if (this.closeTimer != null) return;
        this.closeTimer = window.setTimeout(() => {
            this.closeTimer = null;
            this.statusPopover?.hide();
        }, 220);
    }

    public onPopoverEnter(): void {
        if (this.closeTimer != null) { window.clearTimeout(this.closeTimer); this.closeTimer = null; }
    }

    public onPopoverLeave(): void {
        this.onStatusLeave();
    }
}
